/**
 * A comment thread on a claim, report or travel request (2026-09-28, Keka parity) — the claimant
 * and approver(s) exchanging messages, distinct from "send back for clarification" (which cycles a
 * claim to `draft` and supersedes its workflow instance): posting here does nothing to `status` or
 * `workflowInstanceId`.
 *
 * GENERIC BY entityType/entityId, NOT A PRISMA RELATION — same shape `WorkflowInstance` already uses
 * for "one thing, many unrelated entity tables" (invariant F12 in this module's own other contracts):
 * one thread type serves `ExpenseClaim`/`ExpenseReport`/`TravelRequest`, and neither side commits to
 * the other's lifecycle.
 *
 * VISIBILITY. The claimant, whoever filed it on their behalf (a claim only), and anyone whose
 * `expense:read`/`expense:approve`/`travel:approve` grant reaches the entity may read and post — the
 * same population each entity's own `getClaim`/`getReport`/`getRequest` route already resolves
 * visibility with. Posting does not require a DIFFERENT, narrower permission than reading: this
 * module has no separate "internal desk" population the way payroll's salary-query desk does.
 *
 * APPEND-ONLY BY CONSTRUCTION (v1: no edit, no delete) — same convention
 * `PayrollSalaryQueryMessage` already uses ("a thread message is append-only by construction and its
 * own row carries its author and instant"). No internal notes, no read receipts, for the same reason:
 * nothing in this module has asked for either yet, and both are additive if they ever are.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const EXPENSE_COMMENT_ENTITY_TYPES = [
  'expense_claim',
  'expense_report',
  'travel_request',
  // An advance is the one approvable thing in this module that had nowhere to say "why was this
  // rejected" or "I need this before Friday". `ExpenseComment.entityType` is a plain string
  // column, so this needs no migration — only the resolver branch in the route.
  'expense_advance',
] as const;
export const expenseCommentEntityTypeSchema = z.enum(EXPENSE_COMMENT_ENTITY_TYPES);
export type ExpenseCommentEntityType = z.infer<typeof expenseCommentEntityTypeSchema>;

export const expenseCommentSchema = z.object({
  id: uuidSchema,
  entityType: expenseCommentEntityTypeSchema,
  entityId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  createdAt: instantSchema,
});
export type ExpenseComment = z.infer<typeof expenseCommentSchema>;

export const listExpenseCommentsQuerySchema = z.object({
  entityType: expenseCommentEntityTypeSchema,
  entityId: uuidSchema,
});

export const createExpenseCommentBodySchema = z.object({
  entityType: expenseCommentEntityTypeSchema,
  entityId: uuidSchema,
  body: z.string().trim().min(1).max(5000),
});

const errors = {
  401: errorBodySchema,
  /** No visibility into this entity at all — same "404, not 403" instinct the rest of this module
   * uses for a target outside the caller's own scope. */
  404: errorBodySchema,
};

export const expenseCommentContract = c.router(
  {
    listComments: {
      method: 'GET',
      path: '/comments',
      query: listExpenseCommentsQuerySchema,
      responses: {
        200: z.object({ items: z.array(expenseCommentSchema) }),
        ...errors,
      },
      summary: 'Every comment on a claim, report or travel request, oldest first',
      metadata: routeMeta(null),
    },

    addComment: {
      method: 'POST',
      path: '/comments',
      body: createExpenseCommentBodySchema,
      responses: {
        201: expenseCommentSchema,
        ...errors,
      },
      summary: 'Post a comment on a claim, report or travel request',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/expense' },
);
